import { OtysGetCandidateResponse } from '@/interfaces/candidate'
import {
  matchCriteriaIndexedByNum,
  MatchCriteriaKey,
  SingleMatchCriteria,
  MatchCriteriaProps,
  MatchCriteriaDataModel,
  OtysCreateMatchCriteriaObjModel,
  matchCriteriaAsKeys,
  MatchCriteria,
  prodMatchCriteriaIndexedByNum,
  matchCriteriaFilterNames,
} from '@/interfaces/match-criteria'
import getValueByEnv, {
  mcRecruitmentId,
  mcRvdbId,
  mcZzpId,
} from '../environment'

const vacancyTypeFilterIds = [
  { name: 'Vaste vacature', id: mcRecruitmentId }, // recruitment --> vacte vacature
  { name: 'Interim vacature', id: mcZzpId }, // zzp --> interim vacature
  { name: 'Rvdb vacature', id: mcRvdbId }, // rvdb --> rvdb vacature
]

const vacancyTypeIndex = '17'

export function createSingleMatchCriteriaFilterObj(
  data: SingleMatchCriteria[]
) {
  const index = data[0].uid.split('_')[0]
  const option = matchCriteriaFilterNames[index as MatchCriteriaKey]

  // We need to filter and rename some matching criteria of type vacancy type (id 17)
  const filteredData = data.filter((mc: SingleMatchCriteria) => {
    if (index !== vacancyTypeIndex) return true

    const match = vacancyTypeFilterIds.find((v) => v.id === mc.uid)
    if (match) {
      mc.value = match.name
      return true
    }

    return false
  })

  return {
    filterName: option,
    filterData: filteredData.map((mc: SingleMatchCriteria) => ({
      name: mc.uid,
      value: mc.value,
    })),
  }
}

export function createSingleMatchCriteriaResDataObj(
  data: SingleMatchCriteria[]
): SingleMatchCriteria[] {
  const index: string = data[0].uid.split('_')[0]
  const option = matchCriteriaIndexedByNum[index as MatchCriteriaKey]

  // return an array of single match criteria values
  return data.map((mc: SingleMatchCriteria) => {
    mc.category = option
    return mc
  })
}

export function createMatchCriteriaListResDataObj(
  data: SingleMatchCriteria[]
): MatchCriteriaProps {
  const matchCriteriaDataObj = {
    industry: [],
    max_travel_time: [],
    notice_period: [],
    current_hourly_rate: [],
    employment: [],
    desired_hourly_rate: [],
    current_salary: [],
    max_total_hours_per_week: [],
    language_skills: [],
    educational_attainment: [],
    function_level: [],
    region: [],
    specialism: [],
    talentpool: [],
    platform_published_on: [],
    job_type: [],
    desired_salary: [],
  } as MatchCriteriaDataModel

  data.map((mc: SingleMatchCriteria) => {
    const index: string = mc.uid.split('_')[0]
    const option = matchCriteriaIndexedByNum[index as MatchCriteriaKey]
    if (!matchCriteriaDataObj[option]) return
    mc.category = option
    matchCriteriaDataObj[option].push(mc)
  })

  return matchCriteriaDataObj
}

export function mapMatchCriteriaToOtysObj(
  matchCriteria: MatchCriteriaProps
): OtysCreateMatchCriteriaObjModel {
  const mc: OtysCreateMatchCriteriaObjModel = {
    matchCriteria_1: {},
    matchCriteria_2: {},
    matchCriteria_3: {},
    matchCriteria_4: {},
    matchCriteria_5: {},
    matchCriteria_6: {},
    matchCriteria_7: {},
    matchCriteria_8: {},
    matchCriteria_9: {},
    matchCriteria_10: {},
    matchCriteria_11: {},
    matchCriteria_12: {},
    matchCriteria_13: {},
    matchCriteria_14: {},
    matchCriteria_15: {},
    matchCriteria_16: {},
    matchCriteria_17: {},
    matchCriteria_18: {},
  }

  Object.entries(matchCriteria).forEach(
    ([key, value]: [string, SingleMatchCriteria[] | []]) => {
      const matchCriteriaKey = matchCriteriaAsKeys[
        key as MatchCriteria
      ] as MatchCriteriaKey
      const category = `matchCriteria_${matchCriteriaKey}`
      const obj = Object.fromEntries(
        value.map(({ uid, value }) => [uid, value])
      )

      return (mc[category] = obj)
    }
  )

  return mc
}

export const createMatchCriteriaObject = (
  otysResponse: OtysGetCandidateResponse
): MatchCriteriaDataModel => {
  const matchCriteria = {
    current_hourly_rate: [],
    current_salary: [],
    desired_hourly_rate: [],
    desired_salary: [],
    educational_attainment: [],
    employment: [],
    function_level: [],
    industry: [],
    job_type: [],
    language_skills: [],
    max_total_hours_per_week: [],
    max_travel_time: [],
    notice_period: [],
    platform_published_on: [],
    region: [],
    specialism: [],
    talentpool: [],
  } as MatchCriteriaDataModel

  Object.entries(otysResponse).map(
    ([key, value]: [string, Record<string, string>]) => {
      // 1- find matchCriteria objects
      if (!key.match(/matchCriteria/i)) return

      // 2- find the correspondant category for each of them
      const index: string = key.split('_')[1]

      const category = getValueByEnv({
        dev: matchCriteriaIndexedByNum[index as MatchCriteriaKey],
        test: matchCriteriaIndexedByNum[index as MatchCriteriaKey],
        stag: prodMatchCriteriaIndexedByNum[index as MatchCriteriaKey],
        prod: prodMatchCriteriaIndexedByNum[index as MatchCriteriaKey],
      }) as MatchCriteria

      if (!matchCriteria[category]) return

      // 2- create MC obj for each one and push
      Object.entries(value).map(([key, value]: [string, string]) => {
        const singleMatchCriteriaObj = {
          uid: key,
          category: category,
          value: value,
        } as SingleMatchCriteria

        matchCriteria[category].push(singleMatchCriteriaObj)
      })
    }
  )

  return matchCriteria
}
